/**
 * Video element for rendering mode.
 *
 * In rendering mode, the video must seek to the exact frame time
 * and signal readiness via delayRender/continueRender before the
 * renderer captures the screenshot.
 *
 * Written from scratch for Steve.AI.
 */

import React, {
  forwardRef,
  useContext,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
} from 'react';
import { getAbsoluteSrc } from '../absolute-src.js';
import { useFrameForVolumeProp, useMediaStartsAt } from '../audio/use-audio-frame.js';
import { CompositionManager } from '../composition-context.js';
import { continueRender, delayRender } from '../delay-render.js';
import { useRemotionEnvironment } from '../get-environment.js';
import { isApproximatelyTheSame } from '../is-approximately-the-same.js';
import { random } from '../random.js';
import { SequenceContext } from '../SequenceContext.js';
import { useTimelinePosition } from '../timeline-position-state.js';
import { useCurrentFrame } from '../use-current-frame.js';
import { useVideoConfig } from '../use-video-config.js';
import { evaluateVolume } from '../volume-prop.js';
import { warnAboutNonSeekableMedia } from '../warn-about-non-seekable-media.js';
import { getMediaTime } from './get-current-time.js';

const VideoForRenderingForwardFunction = (
  { onError, volume: volumeProp, allowAmplificationDuringRender, playbackRate, onDuration, ...props },
  ref
) => {
  const absoluteFrame = useTimelinePosition();
  const frame = useCurrentFrame();
  const volumePropsFrame = useFrameForVolumeProp();
  const videoConfig = useVideoConfig();
  const videoRef = useRef(null);
  const sequenceContext = useContext(SequenceContext);
  const mediaStartsAt = useMediaStartsAt();
  const environment = useRemotionEnvironment();
  const { registerAsset, unregisterAsset } = useContext(CompositionManager);

  const id = useMemo(
    () => `video-${random(props.src ?? '')}-${sequenceContext?.cumulatedFrom}-${sequenceContext?.relativeFrom}-${sequenceContext?.durationInFrames}`,
    [props.src, sequenceContext?.cumulatedFrom, sequenceContext?.relativeFrom, sequenceContext?.durationInFrames]
  );

  const volume = evaluateVolume({
    volume: volumeProp,
    frame: volumePropsFrame,
    mediaVolume: 1,
    allowAmplificationDuringRender: allowAmplificationDuringRender ?? false,
  });

  // Register asset for audio extraction
  useEffect(() => {
    if (!props.src || props.muted || volume <= 0) return;
    if (!window.picmaker_audioEnabled) return;

    registerAsset({
      type: 'video',
      src: getAbsoluteSrc(props.src),
      id,
      frame: absoluteFrame,
      volume,
      mediaFrame: frame,
      playbackRate: playbackRate ?? 1,
      allowAmplificationDuringRender: allowAmplificationDuringRender ?? false,
    });
    return () => unregisterAsset(id);
  }, [props.muted, props.src, registerAsset, id, unregisterAsset, volume, frame, absoluteFrame, playbackRate, allowAmplificationDuringRender]);

  useImperativeHandle(ref, () => videoRef.current, []);

  // Seek video to correct time and block rendering until seeked
  useEffect(() => {
    if (!window.picmaker_videoEnabled) return;
    const current = videoRef.current;
    if (!current) return;

    const currentTime = getMediaTime({
      fps: videoConfig.fps,
      frame,
      src: props.src,
      playbackRate: playbackRate || 1,
      startFrom: -mediaStartsAt,
      mediaType: 'video',
    });

    const handle = delayRender(`Rendering <Video /> with src="${props.src}"`);

    if (isApproximatelyTheSame(current.currentTime, currentTime)) {
      if (current.readyState >= 2) {
        continueRender(handle);
        return;
      }
      const onLoaded = () => continueRender(handle);
      current.addEventListener('loadeddata', onLoaded, { once: true });
      return () => current.removeEventListener('loadeddata', onLoaded);
    }

    current.currentTime = currentTime;

    const onSeeked = () => {
      warnAboutNonSeekableMedia(current, 'exception');
      // macOS VideoToolbox needs a small delay for frame accuracy
      if (window.navigator.platform.startsWith('Mac')) {
        setTimeout(() => continueRender(handle), 100);
      } else {
        continueRender(handle);
      }
    };

    const onEnded = () => continueRender(handle);
    const onErrorEvt = () => {
      if (current?.error) {
        console.error('Video error:', current.error);
        if (!onError) {
          throw new Error(`Video error for ${props.src}: ${current.error.message}`);
        }
      }
    };

    current.addEventListener('seeked', onSeeked, { once: true });
    current.addEventListener('ended', onEnded, { once: true });
    current.addEventListener('error', onErrorEvt, { once: true });

    return () => {
      current.removeEventListener('seeked', onSeeked);
      current.removeEventListener('ended', onEnded);
      current.removeEventListener('error', onErrorEvt);
      continueRender(handle);
    };
  }, [volumePropsFrame, props.src, playbackRate, videoConfig.fps, frame, mediaStartsAt, onError]);

  // Load duration metadata
  const { src } = props;
  if (environment === 'rendering') {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useLayoutEffect(() => {
      const newHandle = delayRender('Loading <Video> duration with src=' + src);
      const current = videoRef.current;

      const didLoad = () => {
        if (current?.duration) onDuration(src, current.duration);
        continueRender(newHandle);
      };

      if (current?.duration) {
        onDuration(src, current.duration);
        continueRender(newHandle);
      } else if (current) {
        current.addEventListener('loadedmetadata', didLoad, { once: true });
      }

      return () => {
        if (current) current.removeEventListener('loadedmetadata', didLoad);
        continueRender(newHandle);
      };
    }, [src, onDuration]);
  }

  return <video ref={videoRef} {...props} onError={onError} />;
};

export const VideoForRendering = forwardRef(VideoForRenderingForwardFunction);
